import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Copy, Send, Loader2, AlertTriangle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { SupportLookup } from '../components/SupportLookup';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—');
const Card = ({ children, testid }) => <div data-testid={testid} className="p-4 rounded-xl border border-white/10 bg-[#0A0A0F]">{children}</div>;

function Overview() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get('/admin/hub/overview').then((r) => setO(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!o) return <Loader2 className="h-6 w-6 animate-spin text-[#9333EA]" />;
  const stats = [['Open refund requests', o.open_refund_requests], ['Refunds reported missing', o.missing_refund_reports], ['Refunded (30 days)', money(o.refunded_last_30d_dollars)],
    ['Users', o.total_users], ['Premium users', o.premium_users], ['Group vaults', o.group_vaults]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="hub-overview">
      {stats.map(([k, v]) => <Card key={k} testid={`hub-stat-${k.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '')}`}><p className="text-[10px] uppercase tracking-widest text-[#A19DB0] font-semibold">{k}</p><p className="font-mono text-2xl text-white font-bold mt-1">{v}</p></Card>)}
    </div>
  );
}

function Requests() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get('/admin/hub/refund-requests').then((r) => setRows(r.data.requests)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-[#9333EA]" />;
  if (rows.length === 0) return <p className="text-sm text-[#A19DB0]" data-testid="hub-requests-empty">No open refund requests.</p>;
  return (
    <div className="space-y-3" data-testid="hub-requests-list">
      {rows.map((r) => (
        <Card key={r.request_id} testid={`hub-request-${r.request_id}`}>
          <div className="flex justify-between gap-3"><div><p className="text-white font-semibold">{r.user_name || r.user_email}</p><p className="text-xs text-[#A19DB0]">{r.user_email} · "{r.goal_name}" · {day(r.created_at)}</p></div><p className="font-mono text-white">{money(r.requested_amount)}</p></div>
          {r.reason && <p className="text-xs text-[#D8D4E3] mt-2">“{r.reason}”</p>}
          <div className="flex flex-wrap gap-2 mt-3">
            {r.charges.map((c) => <Link key={c.charge_id} to={`/admin/refund?charge=${c.charge_id}`} data-testid={`hub-refund-charge-${c.charge_id}`} className="px-3 py-1 rounded-full bg-[#9333EA]/20 border border-[#9333EA]/40 text-[#C8A6FF] text-[11px] font-mono hover:bg-[#9333EA]/30">Refund {money(c.amount)} · {c.charge_id.slice(0, 14)}…</Link>)}
            {r.charges.length === 0 && <span className="text-[11px] text-[#6F6981]">No paid charges found for this vault</span>}
          </div>
        </Card>
      ))}
    </div>
  );
}

function ReplyDialog({ group, onClose }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!group) return;
    setDraft(null);
    api.post('/admin/hub/reply-draft', { user_email: group.user_email, refund_ids: group.refunds.map((r) => r.refund_id) })
      .then((r) => setDraft(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [group]); // eslint-disable-line react-hooks/exhaustive-deps
  const copy = async () => { await navigator.clipboard.writeText(draft.body); toast.success('Reply copied'); };
  const send = async () => {
    setBusy(true);
    try { await api.post('/admin/hub/send-reply', draft); toast.success(`Sent to ${draft.to}`); onClose(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!group} onOpenChange={onClose}>
      <DialogContent className="glass-modal sm:max-w-lg" data-testid="hub-reply-dialog">
        <DialogHeader><DialogTitle className="text-white font-heading">Reply to {group?.user_name || group?.user_email}</DialogTitle></DialogHeader>
        {!draft ? <Loader2 className="h-6 w-6 animate-spin text-[#9333EA] mx-auto" /> : (
          <div className="space-y-3">
            <Input data-testid="hub-reply-to-input" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className="input-dark h-10 text-sm" />
            {group?.backup_email && <button data-testid="hub-reply-use-backup" onClick={() => setDraft({ ...draft, to: group.backup_email })} className="text-[11px] text-[#C8A6FF]">Use backup email ({group.backup_email})</button>}
            <Input data-testid="hub-reply-subject-input" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="input-dark h-10 text-sm" />
            <textarea data-testid="hub-reply-body-input" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={12} className="w-full rounded-lg bg-[#0A0A0F] border border-white/10 p-3 text-sm text-white focus:outline-none focus:border-[#9333EA]" />
            <div className="flex gap-2">
              <button data-testid="hub-reply-copy-btn" onClick={copy} className="flex-1 h-10 rounded-lg border border-white/15 text-white text-sm flex items-center justify-center gap-1.5 hover:bg-white/5"><Copy className="h-4 w-4" />Copy</button>
              <button data-testid="hub-reply-send-btn" disabled={busy} onClick={send} className="flex-1 h-10 rounded-lg btn-primary text-sm flex items-center justify-center gap-1.5 disabled:opacity-50"><Send className="h-4 w-4" />{busy ? 'Sending…' : 'Send'}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RecentRefunds() {
  const [rows, setRows] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  useEffect(() => { api.get('/admin/hub/recent-refunds?days=60').then((r) => setRows(r.data.refunds)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-[#9333EA]" />;
  return (
    <div className="space-y-3" data-testid="hub-recent-refunds">
      {rows.length === 0 && <p className="text-sm text-[#A19DB0]">No refunds in the last 60 days.</p>}
      {rows.map((g) => (
        <Card key={`${g.user_email}-${g.date}`} testid={`hub-refund-group-${g.user_email}-${g.date.slice(0, 10)}`}>
          <div className="flex justify-between gap-3">
            <div><p className="text-white font-semibold">{g.user_name || g.user_email}{g.missing_reported && <span className="ml-2 text-[10px] text-red-300 uppercase font-bold"><AlertTriangle className="inline h-3 w-3" /> reported missing</span>}</p>
              <p className="text-xs text-[#A19DB0]">{g.goal_names.join(', ')} · refunded {day(g.date)} · lands {day(g.earliest)} – {day(g.latest)}</p></div>
            <p className="font-mono text-white">{money(g.total)}</p>
          </div>
          <button data-testid={`hub-reply-btn-${g.user_email}-${g.date.slice(0, 10)}`} onClick={() => setReplyTo(g)} className="mt-3 px-3 py-1.5 rounded-full bg-[#9333EA] text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-[#7E22CE]"><Mail className="h-3.5 w-3.5" />Reply</button>
        </Card>
      ))}
      <ReplyDialog group={replyTo} onClose={() => setReplyTo(null)} />
    </div>
  );
}

function LeakReport() {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { const r = await api.get('/admin/hub/leak-report'); setReport(r.data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const recover = async (email) => {
    if (!window.confirm(`Move the leftover money from ${email}'s Stripe account back to the platform?`)) return;
    try { const r = await api.post('/admin/hub/recover', { email }); toast.success(`Recovered ${money(r.recoverable_total_dollars)}`); run(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="hub-leak-report">
      <p className="text-xs text-[#A19DB0]">Finds platform money left in users' Stripe accounts by past refunds. Preview only — nothing moves until you tap Recover. Never touches money still in a user's vaults.</p>
      <button data-testid="hub-leak-run-btn" disabled={busy} onClick={run} className="px-4 py-2 rounded-full btn-primary text-xs font-semibold disabled:opacity-50">{busy ? 'Checking Stripe…' : report ? 'Re-run report' : 'Run report'}</button>
      {report && (
        <>
          <p className="text-white text-sm" data-testid="hub-leak-total">Total recoverable: <span className="font-mono font-bold text-emerald-300">{money(report.total_recoverable_dollars)}</span></p>
          {report.users.map((u) => (
            <Card key={u.email} testid={`hub-leak-row-${u.email}`}>
              <div className="flex items-center justify-between gap-3">
                <div><p className="text-white text-sm">{u.email}</p><p className="text-[11px] text-[#A19DB0]">{u.error ? `Skipped: ${u.error}` : `Stripe ${money(u.connect_available_dollars)} · vaults ${money(u.vault_total_dollars)}`}</p></div>
                <div className="flex items-center gap-3"><span className="font-mono text-white">{money(u.recoverable_dollars)}</span>
                  {u.recoverable_dollars > 0 && <button data-testid={`hub-recover-btn-${u.email}`} onClick={() => recover(u.email)} className="px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-xs font-semibold">Recover</button>}</div>
              </div>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

export default function AdminHubPage() {
  const { user } = useAuth();
  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-vault flex flex-col items-center justify-center gap-3" data-testid="admin-hub-denied">
        <p className="text-white font-semibold">Admins only</p>
        <Link to="/dashboard" className="text-sm text-[#C8A6FF]">Back to dashboard</Link>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-vault" data-testid="admin-hub-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/dashboard" data-testid="admin-hub-back-link" className="text-[#A19DB0] hover:text-white transition-colors"><ArrowLeft className="h-5 w-5" /></Link>
          <span className="font-heading text-lg font-bold text-white tracking-tight">Admin Hub</span>
          <span className="ml-auto flex gap-3 text-xs"><Link to="/admin/refund" className="text-[#A19DB0] hover:text-white">Refund tool</Link><Link to="/admin/waitlist" className="text-[#A19DB0] hover:text-white">Waitlist</Link></span>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-6">
        <Tabs defaultValue="lookup">
          <TabsList className="bg-[#0A0A0F] border border-white/10 mb-5 flex-wrap h-auto">
            <TabsTrigger data-testid="hub-tab-lookup" value="lookup">Support lookup</TabsTrigger>
            <TabsTrigger data-testid="hub-tab-overview" value="overview">Overview</TabsTrigger>
            <TabsTrigger data-testid="hub-tab-requests" value="requests">Refund requests</TabsTrigger>
            <TabsTrigger data-testid="hub-tab-refunds" value="refunds">Recent refunds</TabsTrigger>
            <TabsTrigger data-testid="hub-tab-leaks" value="leaks">Leak report</TabsTrigger>
          </TabsList>
          <TabsContent value="lookup"><SupportLookup /></TabsContent>
          <TabsContent value="overview"><Overview /></TabsContent>
          <TabsContent value="requests"><Requests /></TabsContent>
          <TabsContent value="refunds"><RecentRefunds /></TabsContent>
          <TabsContent value="leaks"><LeakReport /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
